import { useState } from "react";
import { ChatWidgetText, PlanCompareWidget } from "../../../Helper/ChatWidget";
import { CheckIcon } from "../ChatWidgetIcons";
import {
  Button,
  Card,
  ChatWidgetAnswer,
  cx,
  Foot,
} from "../ChatWidgetPrimitives";
import "./PlanCompareCard.scss";

type Plan = PlanCompareWidget["plans"][number];

const defaultPick = (plans: Plan[]) =>
  (
    plans.find((plan) => plan.recommended) ??
    plans.find((plan) => !plan.current) ??
    plans[0]
  )?.id;

/**
 * 10 Plan compare: two or three plans side by side, the current one marked.
 * Tap a plan to pick it; the button switches to it. Answered: the chosen plan
 * stays marked, the rest dims.
 */
export const PlanCompareCard = ({
  widget,
  onAnswer,
  text,
}: {
  widget: PlanCompareWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const plans = (widget.plans ?? []).slice(0, 3);
  const [pickedId, setPickedId] = useState(defaultPick(plans));
  const answered = widget.response?.values?.selected;
  const chosenId = Array.isArray(answered) ? answered[0] : undefined;
  const isAnswered = !!widget.response;
  // The tag sits on its own row above the name; every plan keeps the row so
  // names and prices line up.
  const hasTags =
    isAnswered || plans.some((plan) => plan.current || plan.recommended);
  const activeId = isAnswered ? chosenId : pickedId;
  const picked = plans.find((plan) => plan.id === pickedId);
  const ctaLabel = picked
    ? widget.ctaLabel && picked.id === defaultPick(plans)
      ? widget.ctaLabel
      : text("widgetSwitchTo", { plan: picked.name })
    : undefined;

  return (
    <Card
      className={cx("cw-plans", isAnswered && "cw-plans--answered")}
      label={widget.title}
    >
      {widget.title && (
        <div className="cw-h cw-plans-title">{widget.title}</div>
      )}
      <div
        className="cw-plans-grid"
        role="radiogroup"
        aria-label={widget.title}
        style={{
          gridTemplateColumns: `repeat(${plans.length}, minmax(0, 1fr))`,
        }}
      >
        {plans.map((plan) => {
          const isActive = plan.id === activeId;
          return (
            <button
              type="button"
              role="radio"
              aria-checked={isActive}
              key={plan.id}
              disabled={isAnswered || plan.current}
              className={cx(
                "cw-plan",
                isActive && "cw-plan--on",
                isAnswered && !isActive && "cw-plan--dim",
                plans.length === 3 && "cw-plan--compact",
              )}
              onClick={() => setPickedId(plan.id)}
            >
              {hasTags && (
                <span className="cw-plan-tag-row">
                  {isAnswered && isActive ? (
                    <span className="cw-plan-tag cw-plan-tag--accent">
                      {text("widgetSelected")}
                    </span>
                  ) : plan.current ? (
                    <span className="cw-plan-tag">{text("widgetCurrent")}</span>
                  ) : plan.recommended ? (
                    <span className="cw-plan-tag cw-plan-tag--accent">
                      {text("widgetBestFit")}
                    </span>
                  ) : null}
                </span>
              )}
              <span className="cw-strong cw-plan-name">{plan.name}</span>
              <span className="cw-plan-price">
                <span className="cw-num">{plan.price}</span>
                {plan.interval && (
                  <span className="cw-muted cw-sm"> {plan.interval}</span>
                )}
              </span>
              <span className="cw-plan-features">
                {(plan.features ?? []).map((feature, index) => (
                  <span className="cw-plan-feat" key={index}>
                    <CheckIcon size={13} />
                    <span>{feature}</span>
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
      {!isAnswered && picked && ctaLabel && (
        <Foot>
          <Button
            onClick={() =>
              onAnswer("select", { selected: [picked.id] }, ctaLabel)
            }
          >
            <span>{ctaLabel}</span>
          </Button>
        </Foot>
      )}
      {widget.footnote && (
        <div className="cw-muted cw-sm cw-plans-foot">{widget.footnote}</div>
      )}
    </Card>
  );
};
